import { Button, Callout, getPeriodRange, type PeriodValue, Typography, YStack } from '@shinetools/lumen-react'
import debounce from 'lodash/debounce'
import type { ReactElement } from 'react'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useUserOrganization } from '@modules-deprecated/app/organization'
// These are view-internal to `bills`, not shared exports - reused here because they already do what
// this list needs. Worth extracting into something commonly importable (e.g. features/bills) rather
// than one view reaching into another's internals, but that is a larger move than this change.
import { BillsTable } from '@views/bills/elements/BillsTable'
import { defaultBillsTableColumnConfiguration } from '@views/bills/elements/BillsTable/constants/billsTableColumnConfigurations'
import type { BillsTableSortCriterion } from '@views/bills/elements/BillsTable/types/billsTableSortCriterion'
import { formatFetchBillsResponseToBillsTableData } from '@views/bills/elements/BillsTable/utils/formatFetchBillsResponseToBillsTableData'
import { useFetchBills } from '@views/bills/routes/BillsList/revamped/hooks/useFetchBills'
import { transformBillsTableSortToBillsApiSort } from '@views/bills/routes/BillsList/revamped/utils/transformBillsTableSortToBillsApiSort'

const PAGE_SIZE_OPTIONS = [25, 50, 100] as const
const SEARCH_DEBOUNCE_DELAY_MS = 250
const SEARCH_MINIMUM_CHARACTER_LENGTH = 3

interface BillsListProps {
  supplierId?: string
}

export const BillsList = ({ supplierId }: BillsListProps): ReactElement => {
  const { t } = useTranslation()
  const { organization } = useUserOrganization()
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)
  // `null` means "All" – no receipt date filtering
  const [period, setPeriod] = useState<PeriodValue | null>(null)
  const [searchValue, setSearchValue] = useState('')
  const [debouncedSearchValue, setDebouncedSearchValue] = useState('')
  const [sortCriteria, setSortCriteria] = useState<BillsTableSortCriterion | null>(null)
  const periodRange = useMemo(() => (period ? getPeriodRange(period) : null), [period])
  const updateDebouncedSearchValue = useMemo(
    () =>
      debounce((value: string) => {
        setDebouncedSearchValue(value)
      }, SEARCH_DEBOUNCE_DELAY_MS),
    [],
  )

  const response = useFetchBills({
    filters: {
      contactId: supplierId,
      maxReceiptDate: periodRange?.end,
      minReceiptDate: periodRange?.start,
    },
    isDisabled: !supplierId || !organization?.id,
    organizationId: organization?.id ?? '',
    pagination: { page, pageSize },
    searchQuery: debouncedSearchValue.length >= SEARCH_MINIMUM_CHARACTER_LENGTH ? debouncedSearchValue : undefined,
    sorting: transformBillsTableSortToBillsApiSort(sortCriteria),
  })
  const data = formatFetchBillsResponseToBillsTableData(response)

  useEffect(() => {
    setPage(1)
    setPeriod(null)
    setSearchValue('')
    setDebouncedSearchValue('')
    setSortCriteria(null)
    updateDebouncedSearchValue.cancel()

    return () => updateDebouncedSearchValue.cancel()
  }, [supplierId, updateDebouncedSearchValue])

  const handlePageSizeChange = (nextPageSize: number) => {
    setPageSize(nextPageSize)
    setPage(1)
  }

  const handlePeriodChange = (nextPeriod: PeriodValue | null) => {
    setPeriod(nextPeriod)
    setPage(1)
  }

  const handleRetryButtonClick = () => {
    response.refetch()
  }

  const handleSearchChange = (nextSearchValue: string) => {
    setSearchValue(nextSearchValue)
    setPage(1)
    updateDebouncedSearchValue(nextSearchValue)
  }

  const handleSortCriteriaChange = (nextSortCriteria: BillsTableSortCriterion | null) => {
    setSortCriteria(nextSortCriteria)
    setPage(1)
  }

  return (
    <YStack gap={16}>
      <Typography.Text size="large" weight="semibold">
        {t('bills')}
      </Typography.Text>
      {response.isError ? (
        <YStack gap={16}>
          <Callout color="danger" message={t('suppliers.detail.error.fetch_bills')} />
          <Button label={t('suppliers.detail.error.retry')} onClick={handleRetryButtonClick} variant="tonal" />
        </YStack>
      ) : (
        <BillsTable
          columns={defaultBillsTableColumnConfiguration}
          data={data}
          isLoading={response.isLoading}
          onPageChange={setPage}
          onPageSizeChange={handlePageSizeChange}
          onPeriodChange={handlePeriodChange}
          onSearchChange={handleSearchChange}
          onSortCriteriaChange={handleSortCriteriaChange}
          page={page}
          pageSize={pageSize}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          period={period}
          searchValue={searchValue}
          selectable
          sortCriteria={sortCriteria}
          totalCount={response.pagination?.total ?? 0}
        />
      )}
    </YStack>
  )
}
